import { requestApiJson } from './client';

export type PlayguideCode = 'teket' | 'pia' | 'lawson' | 'eplus';
export type AccountCatalogPlayguideCode = 'eplus' | 'pia' | 'lawson';

const accountCatalogGuideLabel = (guideCode: AccountCatalogPlayguideCode) =>
  guideCode === 'pia' ? 'チケットぴあ' : guideCode === 'lawson' ? 'ローチケ' : 'イープラス';

export type PlayguideConnection = {
  id: number;
  guideCode: PlayguideCode;
  label: string;
  connectionStatus: 'unconfigured' | 'ready' | 'authentication_required' | 'disabled' | 'error';
  automationEnabled: boolean;
  automationDisabledAt: string | null;
  initialAcquisitionCompletedAt: string | null;
  initialAcquisitionEligible: boolean;
  initialAcquisitionAvailableAt: string | null;
  initialAcquisitionRequest?: {
    id: number;
    status: 'queued' | 'running' | 'succeeded' | 'failed';
    errorCode: string | null;
    requestedAt: string;
    startedAt: string | null;
    completedAt: string | null;
  } | null;
  defaultSiteFee: number;
  passwordExpiresOn: string | null;
  credentialsConfigured: boolean;
  credentialsUpdatedAt: string | null;
  credentialsUpdatedBy: { id: number; name: string } | null;
  lastAuthenticatedAt: string | null;
  lastSuccessfulSyncAt: string | null;
  lastConnectionErrorCode: string | null;
  lastConnectionErrorAt: string | null;
  attentionRequired: boolean;
  createdAt: string;
  updatedAt: string;
};

export type PlayguideOperationalAlert = {
  connectionId: number;
  guideCode: PlayguideCode;
  connectionLabel: string;
  kind: 'connection_failure' | 'password_expiration';
  severity: 'warning' | 'danger';
  errorCode: string | null;
  occurredAt: string | null;
  passwordExpiresOn: string | null;
  message: string;
};

export type PlayguideAcquiredPerformanceLinkStatus = 'all' | 'linked' | 'unlinked';

export type PlayguideAcquiredPerformanceResult = {
  connectionId: number;
  guideCode: PlayguideCode;
  connectionLabel: string;
  catalogAvailable: boolean;
  catalogUnavailableReason: 'event_link_required' | 'adapter_unavailable' | null;
  linkStatus: PlayguideAcquiredPerformanceLinkStatus;
  page: number;
  limit: number;
  summary: { total: number; linked: number; unlinked: number };
  items: Array<{
    externalPerformanceId: number;
    eventName: string;
    venueName: string | null;
    performanceAt: string | null;
    sourceStatus: string | null;
    firstSeenAt: string | null;
    lastSeenAt: string | null;
    lastAcquiredAt: string | null;
    latestSnapshotDate: string | null;
    ticketVariantCount: number;
    linkStatus: 'linked' | 'unlinked';
    linkedEvent: {
      sourceId: number;
      eventUuid: string;
      eventName: string | null;
      sourceStatus: string | null;
    } | null;
  }>;
  hasNext: boolean;
};

export type EventPlayguideLinkSuggestion = {
  externalPerformanceId: number;
  connectionId: number;
  guideCode: AccountCatalogPlayguideCode;
  guideLabel: string;
  connectionLabel: string;
  eventName: string;
  casticartEventName: string;
  casticartPerformanceName: string | null;
  casticartPerformanceDate: string | null;
  casticartPerformanceStartTime: string | null;
  performanceSelectionRequired: boolean;
  venueName: string | null;
  performanceAt: string | null;
  lastAcquiredAt: string | null;
  ticketVariantCount: number;
  confidence: number;
  casticartPerformanceId: number | null;
};

export type EventPlayguideLinkSuggestionGroup = {
  eventUuid: string;
  suggestionCount: number;
  suggestions: EventPlayguideLinkSuggestion[];
};

export type PlayguideOrganization = {
  id: number;
  connectionId: number;
  guideCode: PlayguideCode;
  externalOrganizationId: string;
  label: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export type EventPlayguideOption = {
  organizationId: number;
  guideCode: PlayguideCode;
  label: string;
  connectionLabel: string;
  defaultSiteFee: number;
};

export type EventPlayguideAvailability = {
  organizations: EventPlayguideOption[];
  availableGuideCodes: PlayguideCode[];
};

export type EventTicketSetup = {
  eventUuid: string;
  eventName: string;
  ticketSalesStartAt: string | null;
  ticketSalesEndAt: string | null;
  tickets: Array<{
    id?: number;
    ticketType: string;
    price: number;
    quantity: number;
    sold?: number;
    eventPerformanceId?: number | null;
  }>;
  performances: Array<{
    id: number;
    name: string;
    date: string;
    startTime: string;
  }>;
  sites: Array<{
    id?: number;
    siteName: string;
    siteUrl: string;
    siteFee: number;
    salesEntryMode: 'manual' | 'automatic';
    salesScheduleMode: 'event' | 'custom';
    salesStartAt: string | null;
    salesEndAt: string | null;
    sourceId?: number | null;
    guideCode?: PlayguideCode | null;
  }>;
};

export type EventTicketOption = {
  ticketId: number;
  ticketType: string;
  price: number;
  quantity: number;
  eventPerformanceId?: number | null;
  performanceName?: string | null;
  performanceDate?: string | null;
  performanceStartTime?: string | null;
};

export type EplusTicketCandidate = {
  externalTicketVariantId: number;
  externalTicketKey: string;
  receptionName: string;
  seatType: string;
  areaName: string | null;
  salesCategory: string | null;
  sourcePrice: number;
  cumulativeConfirmedCount: number;
  suggestedCapacity: number | null;
  snapshotDate: string | null;
  suggestedCasticartPerformanceId: number | null;
  suggestedCasticartPerformanceIndex: number | null;
  suggestedPerformanceKey: string | null;
  performanceSuggestionReason: 'performance_name' | 'ordinal' | 'ticket_date' | 'external_date' | 'single_performance' | 'unresolved';
  performanceSuggestionConfidence: 'high' | 'medium' | 'low' | 'none';
};

export type EplusPerformanceCandidate = {
  externalPerformanceId: number;
  connectionId: number;
  connectionLabel: string;
  eventName: string;
  venueName: string;
  performanceAt: string;
  lastAcquiredAt: string | null;
  linkedSourceId: number | null;
  linkedEventUuid: string | null;
  tickets: EplusTicketCandidate[];
};

export type EplusCandidateResult = {
  connections: Array<{ connectionId: number; label: string; defaultSiteFee: number }>;
  performances: Array<{
    performanceId: number | null;
    performanceIndex?: number;
    performanceName: string;
    performanceDate: string;
    startTime: string;
    candidates: EplusPerformanceCandidate[];
  }>;
};

export type EplusTicketLinkWrite = {
  externalTicketKey: string;
  targetMode: 'auto' | 'existing' | 'create' | 'ignore';
  casticartTicketId?: number | null;
  configuredCapacity?: number;
  targetPerformanceMode?: 'auto' | 'event' | 'performance';
  casticartPerformanceId?: number | null;
};

export type EplusPerformanceLinkWrite = {
  casticartPerformanceId?: number | null;
  connectionId: number;
  externalPerformanceId: number;
  ticketMappings: EplusTicketLinkWrite[];
  siteFee?: number;
  salesScheduleMode: 'event' | 'custom';
  salesStartAt?: string | null;
  salesEndAt?: string | null;
};

export type TeketEventCandidate = {
  externalEventId: string;
  name: string;
  eventUrl: string;
  matchScore: number;
};

export type TeketEventSearchResult = {
  guideCode: 'teket';
  organizationId: number;
  organizationLabel: string;
  query: string;
  adapterVersion: string;
  candidates: TeketEventCandidate[];
};

export type TeketInitialSyncResult = {
  runId?: number;
  runStatus?: 'succeeded' | 'quarantined' | 'failed';
  errorCode?: string;
  fetchedTicketCount?: number;
  importedSnapshotCount?: number;
  unmappedTicketCount?: number;
  skipped?: boolean;
  reason?: 'outside_sales_schedule';
};

export type TeketTicketCandidate = {
  externalTicketKey: string;
  performanceDateTime: string;
  displayLabel: string;
  ticketName: string;
  subTicketName: string | null;
  couponName: string | null;
  seatType: string;
  unitPrice: number;
  sourcePlannedCount: number | null;
  soldCount: number;
  individualSoldCount: number;
  unattributedSoldCount: number;
  remainingCount: number | null;
};

export type TeketSetupPreview = {
  guideCode: 'teket';
  organizationId: number;
  organizationLabel: string;
  externalEventId: string;
  adapterVersion: string;
  eventId: string;
  downloadDate: string;
  sourceHash: string;
  ticketCandidates: TeketTicketCandidate[];
};

export type EventPlayguideSource = {
  sourceId: number;
  guideCode: PlayguideCode;
  connectionId?: number;
  organizationLabel: string;
  externalEventId: string;
  externalPerformanceId?: number | null;
  sourceStatus: 'draft' | 'ready' | 'syncing' | 'attention' | 'disabled';
  autoSyncEnabled: boolean;
  lastAttemptedSyncAt: string | null;
  lastSuccessfulSyncAt: string | null;
  lastErrorCode: string | null;
  lastManualSyncAt: string | null;
  manualSyncAvailableAt: string | null;
  eventTicketSiteId: number;
  siteName: string;
  salesScheduleMode: 'event' | 'custom';
  salesStartDate?: string | null;
  salesEndDate?: string | null;
  salesStartAt: string | null;
  salesEndAt: string | null;
  effectiveSalesStartAt: string | null;
  effectiveSalesEndAt: string | null;
  effectiveSalesStartDate?: string | null;
  effectiveSalesEndDate?: string | null;
  tickets: Array<{
    mappingId: number;
    externalTicketKey: string;
    performanceDateTime: string | null;
    ticketName: string;
    subTicketName: string | null;
    couponName: string | null;
    seatType: string | null;
    unitPrice: number | null;
    sourcePlannedCount: number | null;
    configuredCapacity: number;
    currentSoldCount: number;
    casticartTicketId: number | null;
    casticartPerformanceId: number | null;
    active: boolean;
  }>;
  unmappedTicketCount: number;
};

const messageFromPayload = (payload: unknown, fallback: string): string => {
  if (!payload || typeof payload !== 'object') return fallback;
  const record = payload as Record<string, unknown>;
  return String(record.message || record.error || fallback);
};

const codeFromPayload = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object') return undefined;
  const error = (payload as Record<string, unknown>).error;
  if (!error || typeof error !== 'object') return undefined;
  const code = (error as Record<string, unknown>).code;
  return typeof code === 'string' && code ? code : undefined;
};

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; query?: Record<string, unknown> } = {},
  fallback: string
): Promise<T> {
  const result = await requestApiJson<T>(path, options);
  if (!result.response.ok || !result.data) {
    const errorPayload = result.error || result.data;
    throw Object.assign(new Error(messageFromPayload(errorPayload, fallback)), {
      statusCode: result.response.status,
      code: codeFromPayload(errorPayload),
    });
  }
  return result.data;
}

export async function listPlayguideConnections(): Promise<PlayguideConnection[]> {
  const data = await request<{ connections: PlayguideConnection[] }>(
    '/admin/playguide-connections',
    {},
    'プレイガイド接続を取得できませんでした。'
  );
  return data.connections || [];
}

export async function listPlayguideOperationalAlerts(): Promise<PlayguideOperationalAlert[]> {
  const data = await request<{ alerts: PlayguideOperationalAlert[] }>(
    '/admin/playguide-connection-alerts',
    {},
    'プレイガイド連携のお知らせを取得できませんでした。'
  );
  return data.alerts || [];
}

export async function listPlayguideAcquiredPerformances(
  connectionId: number,
  input: {
    linkStatus?: PlayguideAcquiredPerformanceLinkStatus;
    page?: number;
    limit?: number;
  } = {}
): Promise<PlayguideAcquiredPerformanceResult> {
  const data = await request<{ result: PlayguideAcquiredPerformanceResult }>(
    `/admin/playguide-connections/${connectionId}/acquired-performances`,
    {
      query: {
        linkStatus: input.linkStatus || 'all',
        page: input.page || 1,
        limit: input.limit || 50,
      },
    },
    '最新の取得一覧を取得できませんでした。'
  );
  return data.result;
}

export async function listEventPlayguideLinkSuggestions(
  eventUuids: string[]
): Promise<EventPlayguideLinkSuggestionGroup[]> {
  const data = await request<{ events: EventPlayguideLinkSuggestionGroup[] }>(
    '/events/playguide-link-suggestions',
    { method: 'POST', body: { eventUuids } },
    '取得済み公演の連動候補を確認できませんでした。'
  );
  return data.events || [];
}

export async function dismissEventPlayguideLinkSuggestions(
  eventUuid: string,
  externalPerformanceIds: number[]
): Promise<{ dismissedCount: number }> {
  return request(
    `/events/${eventUuid}/playguide-link-suggestions/dismiss`,
    { method: 'POST', body: { externalPerformanceIds } },
    '連動候補のお知らせを非表示にできませんでした。'
  );
}

export async function createPlayguideConnection(input: {
  guideCode: PlayguideCode;
  label: string;
  defaultSiteFee?: number;
}): Promise<PlayguideConnection> {
  const data = await request<{ connection: PlayguideConnection }>(
    '/admin/playguide-connections',
    { method: 'POST', body: input },
    'プレイガイド接続を作成できませんでした。'
  );
  return data.connection;
}

export async function updatePlayguideConnection(
  connectionId: number,
  input: {
    label: string;
    automationEnabled: boolean;
    defaultSiteFee?: number;
    passwordExpiresOn?: string | null;
  }
): Promise<PlayguideConnection> {
  const data = await request<{ connection: PlayguideConnection }>(
    `/admin/playguide-connections/${connectionId}`,
    { method: 'PUT', body: input },
    'プレイガイド接続を更新できませんでした。'
  );
  return data.connection;
}

export async function updatePlayguideCredentials(
  connectionId: number,
  input: { loginId: string; password: string }
): Promise<PlayguideConnection> {
  const data = await request<{ connection: PlayguideConnection }>(
    `/admin/playguide-connections/${connectionId}/credentials`,
    { method: 'PUT', body: input },
    'ログイン情報を更新できませんでした。'
  );
  return data.connection;
}

export async function listPlayguideOrganizations(
  connectionId?: number
): Promise<PlayguideOrganization[]> {
  const data = await request<{ organizations: PlayguideOrganization[] }>(
    '/admin/playguide-organizations',
    { query: connectionId ? { connectionId } : undefined },
    '団体設定を取得できませんでした。'
  );
  return data.organizations || [];
}

export async function savePlayguideOrganization(input: {
  id?: number;
  connectionId: number;
  externalOrganizationId: string;
  label: string;
  enabled?: boolean;
}): Promise<PlayguideOrganization> {
  const path = input.id
    ? `/admin/playguide-organizations/${input.id}`
    : '/admin/playguide-organizations';
  const data = await request<{ organization: PlayguideOrganization }>(
    path,
    {
      method: input.id ? 'PUT' : 'POST',
      body: {
        connectionId: input.connectionId,
        externalOrganizationId: input.externalOrganizationId,
        label: input.label,
        enabled: input.enabled ?? true,
      },
    },
    '団体設定を保存できませんでした。'
  );
  return data.organization;
}

export async function testPlayguideConnection(
  connectionId: number,
  organizationId?: number
): Promise<PlayguideConnection> {
  const data = await request<{ connection: PlayguideConnection }>(
    `/admin/playguide-connections/${connectionId}/test`,
    { method: 'POST', body: organizationId ? { organizationId } : {} },
    'ログイン確認に失敗しました。'
  );
  return data.connection;
}

export async function runInitialEplusAcquisition(
  connectionId: number
): Promise<Record<string, unknown>> {
  return runInitialPlayguideAcquisition(connectionId);
}

export async function runInitialPlayguideAcquisition(connectionId: number): Promise<{
  requestId?: number;
  connectionId?: number;
  guideCode?: PlayguideCode;
  status?: 'queued' | 'running';
  requestedAt?: string;
  alreadyQueued?: boolean;
  runStatus?: 'succeeded' | 'partial' | 'skipped';
}> {
  const data = await request<{ result: {
    requestId: number;
    connectionId: number;
    guideCode: PlayguideCode;
    status: 'queued' | 'running';
    requestedAt: string;
    alreadyQueued: boolean;
  } }>(
    `/admin/playguide-connections/${connectionId}/initial-sync`,
    { method: 'POST' },
    'プレイガイドの初回取得を実行できませんでした。'
  );
  return data.result;
}

export async function listAccountPlayguidePerformanceCandidates(
  guideCode: AccountCatalogPlayguideCode,
  eventUuid: string,
  connectionId?: number
): Promise<EplusCandidateResult> {
  const label = accountCatalogGuideLabel(guideCode);
  const data = await request<{ result: EplusCandidateResult }>(
    `/events/${eventUuid}/playguide-sources/${guideCode}/candidates`,
    { query: connectionId ? { connectionId } : undefined },
    `${label}の同日公演候補を取得できませんでした。`
  );
  return data.result;
}

export async function listEplusPerformanceCandidates(
  eventUuid: string,
  connectionId?: number
): Promise<EplusCandidateResult> {
  return listAccountPlayguidePerformanceCandidates('eplus', eventUuid, connectionId);
}

export async function listAvailableAccountPlayguidePerformanceCandidates(
  guideCode: AccountCatalogPlayguideCode,
  input: {
    connectionId?: number;
    performances: Array<{
      performanceIndex: number;
      performanceName?: string;
      performanceDate: string;
      startTime?: string;
    }>;
  }
): Promise<EplusCandidateResult> {
  const label = accountCatalogGuideLabel(guideCode);
  const data = await request<{ result: EplusCandidateResult }>(
    `/playguide-sources/${guideCode}/candidates`,
    { method: 'POST', body: input },
    `${label}の同日公演候補を取得できませんでした。`
  );
  return data.result;
}

export async function listAvailableEplusPerformanceCandidates(input: {
  connectionId?: number;
  performances: Array<{
    performanceIndex: number;
    performanceName?: string;
    performanceDate: string;
    startTime?: string;
  }>;
}): Promise<EplusCandidateResult> {
  return listAvailableAccountPlayguidePerformanceCandidates('eplus', input);
}

export async function linkAccountPlayguidePerformance(
  guideCode: AccountCatalogPlayguideCode,
  eventUuid: string,
  input: EplusPerformanceLinkWrite
): Promise<{ sourceId: number; source: EventPlayguideSource; reapplyResult: Record<string, unknown> }> {
  const label = accountCatalogGuideLabel(guideCode);
  const body: EplusPerformanceLinkWrite = {
    casticartPerformanceId: input.casticartPerformanceId,
    connectionId: input.connectionId,
    externalPerformanceId: input.externalPerformanceId,
    ticketMappings: input.ticketMappings.map((mapping) => ({
      externalTicketKey: mapping.externalTicketKey,
      targetMode: mapping.targetMode,
      casticartTicketId: mapping.casticartTicketId,
      configuredCapacity: mapping.configuredCapacity,
      targetPerformanceMode: mapping.targetPerformanceMode,
      casticartPerformanceId: mapping.casticartPerformanceId,
    })),
    siteFee: input.siteFee,
    salesScheduleMode: input.salesScheduleMode,
    salesStartAt: input.salesStartAt,
    salesEndAt: input.salesEndAt,
  };
  return request(
    `/events/${eventUuid}/playguide-sources/${guideCode}`,
    { method: 'POST', body },
    `${label}の自動入力設定を保存できませんでした。`
  );
}

export async function linkEplusPerformance(
  eventUuid: string,
  input: EplusPerformanceLinkWrite
): Promise<{ sourceId: number; source: EventPlayguideSource; reapplyResult: Record<string, unknown> }> {
  return linkAccountPlayguidePerformance('eplus', eventUuid, input);
}

export async function linkEplusPerformanceFromRegistrationDraft(
  eventUuid: string | null | undefined,
  draft: {
    casticartPerformanceIndex: number | null;
    connectionId: number;
    externalPerformanceId: number;
    ticketMappings: Array<{
      externalTicketKey: string;
      targetMode: 'auto' | 'existing' | 'create' | 'ignore';
      casticartTicketId?: number | null;
      targetTicketIndex?: number | null;
      configuredCapacity?: number;
      targetPerformanceMode?: 'auto' | 'event' | 'performance';
      casticartPerformanceId?: number | null;
      targetPerformanceIndex?: number | null;
    }>;
    siteFee?: number;
    salesScheduleMode: 'event' | 'custom';
    salesStartAt?: string | null;
    salesEndAt?: string | null;
  } | null,
  createdTickets: Array<{ id?: unknown }> = [],
  createdPerformances: Array<{ id?: unknown }> = []
) {
  if (!eventUuid || !draft) return null;
  const performanceId = draft.casticartPerformanceIndex == null
    ? null
    : Number(createdPerformances[draft.casticartPerformanceIndex]?.id || 0) || null;
  if (draft.casticartPerformanceIndex != null && !performanceId) {
    throw new Error('作成したCasticart公演を特定できませんでした。');
  }
  const ticketMappings: EplusTicketLinkWrite[] = draft.ticketMappings.map((mapping) => {
    const casticartTicketId = mapping.targetTicketIndex == null
      ? mapping.casticartTicketId
      : Number(createdTickets[mapping.targetTicketIndex]?.id || 0) || null;
    if (mapping.targetTicketIndex != null && !casticartTicketId) {
      throw new Error('作成したCasticart券種を特定できませんでした。');
    }
    return {
      externalTicketKey: mapping.externalTicketKey,
      targetMode: mapping.targetTicketIndex == null ? mapping.targetMode : 'existing',
      casticartTicketId,
      configuredCapacity: mapping.configuredCapacity,
      targetPerformanceMode: mapping.targetPerformanceMode,
      casticartPerformanceId: mapping.targetPerformanceIndex == null
        ? mapping.casticartPerformanceId
        : Number(createdPerformances[mapping.targetPerformanceIndex]?.id || 0) || null,
    };
  });
  return linkEplusPerformance(eventUuid, {
    casticartPerformanceId: performanceId,
    connectionId: draft.connectionId,
    externalPerformanceId: draft.externalPerformanceId,
    ticketMappings,
    siteFee: draft.siteFee,
    salesScheduleMode: draft.salesScheduleMode,
    salesStartAt: draft.salesStartAt,
    salesEndAt: draft.salesEndAt,
  });
}

export async function listEventPlayguideOptions(
  eventUuid: string
): Promise<EventPlayguideOption[]> {
  const data = await request<EventPlayguideAvailability>(
    `/events/${eventUuid}/playguide-options`,
    {},
    '自動入力に利用できる団体を取得できませんでした。'
  );
  return data.organizations || [];
}

export async function getEventPlayguideAvailability(
  eventUuid: string
): Promise<EventPlayguideAvailability> {
  const data = await request<EventPlayguideAvailability>(
    `/events/${eventUuid}/playguide-options`,
    {},
    '自動取得に利用できるサイトを取得できませんでした。'
  );
  return {
    organizations: data.organizations || [],
    availableGuideCodes: data.availableGuideCodes || [],
  };
}

export async function listAvailablePlayguideOptions(): Promise<EventPlayguideOption[]> {
  const data = await request<{ organizations: EventPlayguideOption[] }>(
    '/playguide-options',
    {},
    '自動入力に利用できる団体を取得できませんでした。'
  );
  return data.organizations || [];
}

export async function listEventTicketOptions(eventUuid: string): Promise<EventTicketOption[]> {
  const data = await request<{ tickets: EventTicketOption[] }>(
    `/events/${eventUuid}/playguide-ticket-options`,
    {},
    'チケット種別を取得できませんでした。'
  );
  return data.tickets || [];
}

export async function listEventPlayguideSources(
  eventUuid: string
): Promise<EventPlayguideSource[]> {
  const data = await request<{ sources: EventPlayguideSource[] }>(
    `/events/${eventUuid}/playguide-sources`,
    {},
    '自動入力設定を取得できませんでした。'
  );
  return data.sources || [];
}

export async function deleteEventPlayguideSource(
  eventUuid: string,
  sourceId: number
): Promise<{
  sourceId: number;
  eventTicketSiteId: number;
  linkState: 'detached' | 'deleted';
  historyPreserved: boolean;
}> {
  return request<{
    sourceId: number;
    eventTicketSiteId: number;
    linkState: 'detached' | 'deleted';
    historyPreserved: boolean;
  }>(
    `/events/${eventUuid}/playguide-sources/${sourceId}`,
    { method: 'DELETE' },
    '自動取得サイトの連動を解除できませんでした。'
  );
}

export async function updateEventPlayguideSourceLinkState(
  eventUuid: string,
  sourceId: number,
  linked: boolean
): Promise<{
  sourceId: number;
  linked: boolean;
  sourceStatus: EventPlayguideSource['sourceStatus'];
  historyPreserved: boolean;
  unmappedTicketCount: number;
}> {
  return request<{
    sourceId: number;
    linked: boolean;
    sourceStatus: EventPlayguideSource['sourceStatus'];
    historyPreserved: boolean;
    unmappedTicketCount: number;
  }>(
    `/events/${eventUuid}/playguide-sources/${sourceId}/link-state`,
    { method: 'PATCH', body: { linked } },
    '自動取得サイトの連動状態を変更できませんでした。'
  );
}

export async function getEventTicketSetup(eventUuid: string): Promise<EventTicketSetup> {
  const data = await request<{ setup: EventTicketSetup }>(
    `/events/${eventUuid}/ticket-setup`,
    {},
    'チケット関連設定を取得できませんでした。'
  );
  return data.setup;
}

export async function saveEventTicketSetup(
  eventUuid: string,
  input: Pick<EventTicketSetup, 'ticketSalesStartAt' | 'ticketSalesEndAt' | 'tickets' | 'sites'>
): Promise<EventTicketSetup> {
  const data = await request<{ setup: EventTicketSetup }>(
    `/events/${eventUuid}/ticket-setup`,
    { method: 'PUT', body: input },
    'チケット関連設定を保存できませんでした。'
  );
  return data.setup;
}

export async function searchTeketEventCandidates(
  eventUuid: string,
  organizationId: number
): Promise<TeketEventSearchResult> {
  const data = await request<{ result: TeketEventSearchResult }>(
    `/events/${eventUuid}/playguide-sources/teket/search`,
    { method: 'POST', body: { organizationId } },
    'Teketのイベント候補を取得できませんでした。'
  );
  return data.result;
}

export async function searchAvailableTeketEventCandidates(
  eventName: string,
  organizationId: number
): Promise<TeketEventSearchResult> {
  const data = await request<{ result: TeketEventSearchResult }>(
    '/playguide-sources/teket/search',
    { method: 'POST', body: { eventName, organizationId } },
    'Teketのイベント候補を取得できませんでした。'
  );
  return data.result;
}

export async function previewTeketEventSource(
  eventUuid: string,
  input: { organizationId: number; externalEventId: string }
): Promise<TeketSetupPreview> {
  const data = await request<{ preview: TeketSetupPreview }>(
    `/events/${eventUuid}/playguide-sources/teket/preview`,
    { method: 'POST', body: input },
    'Teketの券種を取得できませんでした。'
  );
  return data.preview;
}

export async function previewAvailableTeketEventSource(input: {
  organizationId: number;
  externalEventId: string;
}): Promise<TeketSetupPreview> {
  const data = await request<{ preview: TeketSetupPreview }>(
    '/playguide-sources/teket/preview',
    { method: 'POST', body: input },
    'Teketの券種を取得できませんでした。'
  );
  return data.preview;
}

export async function createTeketEventSource(
  eventUuid: string,
  input: {
    organizationId: number;
    externalEventId: string;
    capacities: Array<{ externalTicketKey: string; capacity: number }>;
    ticketMappings?: Array<{
      externalTicketKey: string;
      casticartTicketId?: number | null;
      mappingAction?: 'map' | 'create' | 'defer';
      eventCapacityMode?: 'keep' | 'source' | 'manual';
      eventCapacity?: number;
      targetPerformanceMode?: 'auto' | 'event' | 'performance';
      casticartPerformanceId?: number | null;
    }>;
    salesScheduleMode: 'event' | 'custom';
    salesStartAt?: string | null;
    salesEndAt?: string | null;
  }
): Promise<{
  sourceId: number;
  source: EventPlayguideSource;
  warnings: unknown[];
  initialSync: TeketInitialSyncResult;
}> {
  return request(
    `/events/${eventUuid}/playguide-sources/teket`,
    { method: 'POST', body: input },
    'Teketの自動入力設定を保存できませんでした。'
  );
}

export async function createTeketEventSourceFromRegistrationDraft(
  eventUuid: string | null | undefined,
  draft: {
    organizationId: number;
    externalEventId: string;
    capacities: Array<{ externalTicketKey: string; capacity: number }>;
    ticketMappings: Array<{
      externalTicketKey: string;
      casticartTicketId?: number | null;
      targetTicketIndex?: number | null;
      mappingAction?: 'map' | 'create' | 'defer';
      eventCapacityMode?: 'keep' | 'source' | 'manual';
      eventCapacity?: number;
      targetPerformanceMode?: 'auto' | 'event' | 'performance';
      casticartPerformanceId?: number | null;
      targetPerformanceIndex?: number | null;
    }>;
    salesScheduleMode: 'event' | 'custom';
    salesStartAt: string | null;
    salesEndAt: string | null;
  } | null,
  createdTickets: Array<{ id?: unknown }> = [],
  createdPerformances: Array<{ id?: unknown }> = []
) {
  if (!eventUuid || !draft) return null;
  return createTeketEventSource(eventUuid, {
    ...draft,
    ticketMappings: draft.ticketMappings.map((mapping) => ({
      ...mapping,
      casticartTicketId:
        mapping.targetTicketIndex == null
          ? mapping.casticartTicketId || null
          : Number(createdTickets[mapping.targetTicketIndex]?.id || 0) || null,
      mappingAction: mapping.targetTicketIndex == null
        ? mapping.mappingAction
        : 'map',
      casticartPerformanceId: mapping.targetPerformanceIndex == null
        ? mapping.casticartPerformanceId
        : Number(createdPerformances[mapping.targetPerformanceIndex]?.id || 0) || null,
      targetTicketIndex: undefined,
      targetPerformanceIndex: undefined,
    })),
  });
}

export async function updateEventPlayguideSourceSchedule(
  eventUuid: string,
  sourceId: number,
  input: {
    salesScheduleMode: 'event' | 'custom';
    salesStartAt?: string | null;
    salesEndAt?: string | null;
  }
): Promise<EventPlayguideSource> {
  const data = await request<{ source: EventPlayguideSource }>(
    `/events/${eventUuid}/playguide-sources/${sourceId}/schedule`,
    { method: 'PUT', body: input },
    '自動入力の券売期間を更新できませんでした。'
  );
  return data.source;
}

export async function updateEventPlayguideTicketMappings(
  eventUuid: string,
  sourceId: number,
  mappings: Array<{
    externalTicketKey: string;
    casticartTicketId?: number | null;
    mappingAction?: 'map' | 'create' | 'defer';
    newTicketName?: string;
    eventCapacityMode?: 'keep' | 'source' | 'manual';
    eventCapacity?: number;
    targetPerformanceMode?: 'auto' | 'event' | 'performance';
    casticartPerformanceId?: number | null;
  }>
): Promise<{
  source: EventPlayguideSource;
  reapplyResult: { reapplied: boolean; reason?: string };
}> {
  return request<{
    source: EventPlayguideSource;
    reapplyResult: { reapplied: boolean; reason?: string };
  }>(
    `/events/${eventUuid}/playguide-sources/${sourceId}/ticket-mappings`,
    { method: 'PUT', body: { mappings } },
    '券種の紐付けを更新できませんでした。'
  );
}

export async function syncEventPlayguideSource(
  eventUuid: string,
  sourceId: number
): Promise<Record<string, unknown>> {
  const data = await request<{ result: Record<string, unknown> }>(
    `/events/${eventUuid}/playguide-sources/${sourceId}/sync`,
    { method: 'POST' },
    'Teketの同期に失敗しました。'
  );
  return data.result;
}

export async function syncEventPlayguideSources(
  eventUuid: string,
  sourceIds?: number[]
): Promise<{
  sourceCount: number;
  succeeded: number;
  quarantined: number;
  failed: number;
  results: Array<Record<string, unknown>>;
}> {
  const data = await request<{ result: any }>(
    `/events/${eventUuid}/playguide-sources/sync`,
    { method: 'POST', body: sourceIds?.length ? { sourceIds } : {} },
    '券売状況を取得できませんでした。'
  );
  return data.result;
}
